import React from 'react';
import Icon from '../../../components/AppIcon';

const MarketInsights = ({ data, user, loading = false }) => {
  if (loading || !data) {
    return (
      <div className="space-y-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-muted rounded w-1/4 mb-4" />
            <div className="space-y-3">
              <div className="h-4 bg-muted rounded" />
              <div className="h-4 bg-muted rounded w-3/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const marketInsights = data?.marketInsights;
  const userOccupation = user?.occupation || 'Software Engineer';
  const userSeniority = user?.seniority || 'Mid-Level';

  const insights = [
    {
      title: 'Industry Trends',
      icon: 'TrendingUp',
      color: 'bg-primary/10 text-primary',
      content: marketInsights?.industryTrends || 'Loading industry trend analysis...'
    },
    {
      title: 'Salary Benchmarks',
      icon: 'DollarSign',
      color: 'bg-success/10 text-success',
      content: marketInsights?.salaryBenchmarks || 'Loading salary benchmark data...'
    },
    {
      title: 'Demand Indicators',
      icon: 'BarChart3',
      color: 'bg-warning/10 text-warning',
      content: marketInsights?.demandIndicators || 'Analyzing market demand signals...'
    },
    {
      title: 'Timing Factors',
      icon: 'Clock',
      color: 'bg-secondary/10 text-secondary',
      content: marketInsights?.timingFactors || 'Evaluating optimal timing considerations...'
    }
  ];

  const mockBenchmarks = [
    {
      role: userOccupation,
      level: 'Junior',
      salaryRange: '$65,000 - $80,000',
      growth: '+5.2%'
    },
    {
      role: userOccupation,
      level: 'Mid-Level',
      salaryRange: '$80,000 - $110,000',
      growth: '+7.1%'
    },
    {
      role: userOccupation,
      level: 'Senior',
      salaryRange: '$110,000 - $150,000',
      growth: '+8.3%'
    },
    {
      role: userOccupation,
      level: 'Lead/Principal',
      salaryRange: '$150,000 - $200,000+',
      growth: '+6.8%'
    }
  ];

  const marketTrends = [
    {
      trend: 'Remote Work Opportunities',
      impact: 'Positive',
      description: 'Increased flexibility in role negotiations',
      icon: 'Home'
    },
    {
      trend: 'Skills-Based Hiring',
      impact: 'Positive',
      description: 'Focus on demonstrable skills over traditional credentials',
      icon: 'Award'
    },
    {
      trend: 'Economic Uncertainty',
      impact: 'Neutral',
      description: 'Mixed impact on promotion timelines and budgets',
      icon: 'AlertTriangle'
    },
    {
      trend: 'Tech Innovation Demand',
      impact: 'Positive',
      description: 'Growing demand for technical expertise and leadership',
      icon: 'Zap'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Market Insights Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {insights?.map((insight, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${insight?.color}`}>
                <Icon name={insight?.icon} size={16} />
              </div>
              <h3 className="text-lg font-semibold text-foreground">{insight?.title}</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {insight?.content}
            </p>
          </div>
        ))}
      </div>

      {/* Salary Benchmarks */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-success/10 rounded-full flex items-center justify-center">
            <Icon name="DollarSign" size={16} className="text-success" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">
            {userOccupation} Salary Benchmarks
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-medium text-foreground">Level</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Salary Range</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">YoY Growth</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {mockBenchmarks?.map((benchmark, index) => (
                <tr 
                  key={index} 
                  className={`border-b border-border/50 ${
                    benchmark?.level === userSeniority ? 'bg-primary/5' : ''
                  }`}
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-foreground">{benchmark?.level}</span>
                      {benchmark?.level === userSeniority && (
                        <div className="w-2 h-2 bg-primary rounded-full" />
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">
                    {benchmark?.salaryRange}
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm font-medium text-success">
                      {benchmark?.growth}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {benchmark?.level === userSeniority ? (
                      <span className="text-xs px-2 py-1 bg-primary/20 text-primary rounded-full">
                        Current Level
                      </span>
                    ) : index > mockBenchmarks?.findIndex(b => b?.level === userSeniority) ? (
                      <span className="text-xs px-2 py-1 bg-warning/20 text-warning rounded-full">
                        Target
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded-full">
                        Previous
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
          <p className="text-sm text-muted-foreground">
            <Icon name="Info" size={14} className="inline mr-2" />
            Salary ranges are based on industry data from multiple sources and may vary by location, company size, and specific role requirements.
          </p>
        </div>
      </div>

      {/* Market Trends */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-warning/10 rounded-full flex items-center justify-center">
            <Icon name="Globe" size={16} className="text-warning" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Current Market Trends</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {marketTrends?.map((trend, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name={trend?.icon} size={14} className="text-primary" />
                  </div>
                  <h4 className="font-medium text-foreground">{trend?.trend}</h4>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  trend?.impact === 'Positive' ? 'bg-success/20 text-success' :
                  trend?.impact === 'Negative'? 'bg-destructive/20 text-destructive' : 'bg-warning/20 text-warning'
                }`}>
                  {trend?.impact}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {trend?.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Regional Insights */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center">
            <Icon name="MapPin" size={16} className="text-secondary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Regional Market Analysis</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Top Markets */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-3 flex items-center space-x-2">
              <Icon name="TrendingUp" size={16} className="text-success" />
              <span>Growing Markets</span>
            </h4>
            <div className="space-y-2">
              {[
                { city: 'Austin, TX', growth: '+12.5%' },
                { city: 'Denver, CO', growth: '+10.8%' },
                { city: 'Seattle, WA', growth: '+9.2%' }
              ]?.map((market, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">{market?.city}</span>
                  <span className="text-sm font-medium text-success">{market?.growth}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Salary Leaders */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-3 flex items-center space-x-2">
              <Icon name="DollarSign" size={16} className="text-warning" />
              <span>Highest Paying</span>
            </h4>
            <div className="space-y-2">
              {[
                { city: 'San Francisco, CA', premium: '+35%' },
                { city: 'New York, NY', premium: '+25%' },
                { city: 'Seattle, WA', premium: '+18%' }
              ]?.map((market, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">{market?.city}</span>
                  <span className="text-sm font-medium text-warning">{market?.premium}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Remote Opportunities */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-3 flex items-center space-x-2">
              <Icon name="Home" size={16} className="text-primary" />
              <span>Remote Trends</span>
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Fully Remote</span>
                <span className="text-sm font-medium text-primary">68%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Hybrid</span>
                <span className="text-sm font-medium text-primary">45%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Salary Impact</span>
                <span className="text-sm font-medium text-success">+8%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Items */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/30 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="Lightbulb" size={16} className="text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Market-Based Recommendations</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h4 className="font-medium text-foreground">Immediate Actions</h4>
            <div className="space-y-1">
              <div className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  Research current market rates for your role level
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  Network with professionals in growing markets
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  Consider remote opportunities for salary optimization
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <h4 className="font-medium text-foreground">Strategic Considerations</h4>
            <div className="space-y-1">
              <div className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-secondary rounded-full mt-2 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  Time advancement requests with budget cycles
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-secondary rounded-full mt-2 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  Leverage industry growth trends in negotiations
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-secondary rounded-full mt-2 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  Build skills aligned with market demand
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketInsights;